import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { Organization, App } from '../types/api';
import { apiService } from '../services/api';
import { classifyError } from '../lib/errors';
import { noteOrganizationSwitch } from '../hooks/useInfiniteList';
import { useUser } from './UserContext';

export type AppsStatus = 'idle' | 'loading' | 'forbidden' | 'error' | 'success';

interface OrganizationContextType {
  selectedOrganization: Organization | null;
  organizations: Organization[];
  setSelectedOrganization: (org: Organization | null) => void;
  /**
   * Membership loading, plus the *first* app-client load for a newly selected
   * organization. `usePermissions` folds this into its own flag and
   * PermissionGate replaces the whole page while it is true — which is why an
   * explicit refresh deliberately stays out of it. See loadAppsForOrganization.
   */
  isLoading: boolean;
  /** Any app-client fetch, including the refreshes that do not gate the page. */
  appsLoading: boolean;
  /**
   * The user/membership load failed: the list of organizations is unknown.
   * Owned by the shell banner only; route gates render nothing beneath it.
   */
  membershipError: unknown;
  /**
   * The selected organization's app-client load failed (a 403 is not an
   * error — it is an empty list). Owned by the pages that consume app clients
   * (App Clients, Request Builder), whose Retry repeats that fetch. The raw
   * caught value: ErrorState describes it at render.
   */
  appsError: unknown;
  /**
   * Where the selected organization's app-client load stands. 'forbidden' is a
   * 403 (the caller lacks app_clients:read): `apps` is empty because it is
   * unreadable, not because none exist — consumers explain the permission
   * rather than offering "Create one". Empty copy belongs to 'success' only.
   */
  appsStatus: AppsStatus;
  /**
   * Re-reads memberships and permissions. `background` keeps the page mounted
   * (no route skeleton) — for a refresh after a write made on the page itself.
   */
  refreshOrganizations: (options?: { background?: boolean }) => Promise<void>;
  refreshOrganizationApps: (
    organizationId: string,
    options?: { includeCognitoDetails?: boolean },
  ) => Promise<void>;
  createOrganization: (name: string, licenseKey: string) => Promise<Organization>;
  updateAppInOrganization: (organizationId: string, appId: string, updatedApp: App) => void;
  addAppToOrganization: (organizationId: string, newApp: App) => void;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

interface OrganizationProviderProps {
  children: ReactNode;
}

// Despite the name, this is used whole: one browser-wide slot, not one per
// user. Scoping it to the signed-in user means changing the end-to-end suite in
// the same commit — e2e/fixtures/personas.ts seeds this exact key on every
// navigation and three specs assert against it. Until then the exposure stays
// small: a restored id is validated against the *current* user's memberships
// below, so a shared browser can only reopen an organization the person signing
// in already belongs to.
const SELECTED_ORGANIZATION_STORAGE_KEY_PREFIX = 'dory.portal.selectedOrganizationId';

const getStoredSelectedOrganizationId = (storageKey: string): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage.getItem(storageKey);
  } catch (error) {
    console.error('Failed to read selected organization from storage:', error);
    return null;
  }
};

const setStoredSelectedOrganizationId = (storageKey: string, organizationId: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(storageKey, organizationId);
  } catch (error) {
    console.error('Failed to save selected organization to storage:', error);
  }
};

export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({ children }) => {
  const { user, accessToken, isLoading: userLoading, error: userError, refreshUser } = useUser();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [appsLoading, setAppsLoading] = useState(false);
  const [membershipError, setMembershipError] = useState<unknown>(null);
  const [appsError, setAppsError] = useState<unknown>(null);
  const [appsStatus, setAppsStatus] = useState<AppsStatus>('idle');
  const selectedOrganizationStorageKey = SELECTED_ORGANIZATION_STORAGE_KEY_PREFIX;

  const loadOrganizations = useCallback(async () => {
    // Wait for user context to finish loading before processing organizations.
    // Without this guard, the callback runs immediately with user=null, sets
    // isLoading=false, and causes PermissionGate to see zero permissions and
    // redirect to /my-apps before the real user data arrives.
    if (userLoading) {
      setIsLoading(true);
      return;
    }

    try {
      setIsLoading(true);
      setMembershipError(userError);

      const mappedOrganizations: Organization[] = (user?.organizations ?? []).map((membership) => ({
        id: membership.organization.id,
        name: membership.organization.name,
        slug: membership.organization.slug,
        isSystemOrg: membership.organization.isSystemOrg,
        apps: [],
      }));

      setOrganizations((prevOrganizations) => {
        const previousAppsByOrganization = new Map(
          prevOrganizations.map((organization) => [organization.id, organization.apps]),
        );

        return mappedOrganizations.map((organization) => ({
          ...organization,
          apps: previousAppsByOrganization.get(organization.id) || [],
        }));
      });
    } catch (err) {
      setMembershipError(err);
      console.error('Error loading organizations:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user, userError, userLoading]);

  const loadAppsForOrganization = useCallback(
    async (
      organizationId: string,
      options?: { background?: boolean; includeCognitoDetails?: boolean },
    ) => {
      if (!accessToken) {
        return;
      }

      // Two modes, because `isLoading` is what PermissionGate reads as
      // "permissions are not known yet" and it replaces the whole page while it
      // is true.
      //  - Selecting an organization loads its apps for the first time. The
      //    page has nothing to show yet, the route skeleton is the right
      //    surface, and an E2E case pins it (my-apps-cards.spec.ts, "holds the
      //    route skeleton while apps load"). Keep the shared flag.
      //  - Refreshing after a rename or a delete happens with the page already
      //    on screen. Raising the shared flag there makes the gate swap the
      //    page out and React remount a fresh copy — a full-page blink in the
      //    middle of an ordinary action. Only `appsLoading` moves.
      const background = options?.background === true;

      try {
        if (!background) {
          setIsLoading(true);
        }
        setAppsLoading(true);
        setAppsStatus('loading');
        setAppsError(null);
        // No Cognito details by default. This load runs on sign-in and on every
        // organization switch, on whatever page the user is on, and the API
        // answered it with one Cognito call per app client — throttled per AWS
        // account against the single shared user pool. Only My Apps needs those
        // fields, and it asks for them itself.
        const apps = await apiService.getOrganizationAppClients(organizationId, accessToken, {
          includeCognitoDetails: options?.includeCognitoDetails === true,
        });

        setOrganizations((prevOrgs) =>
          prevOrgs.map((org) =>
            org.id === organizationId
              ? {
                  ...org,
                  apps,
                }
              : org,
          ),
        );
        setAppsStatus('success');
      } catch (err: unknown) {
        // A 403 means the user lacks app_clients:read — not an error to
        // surface, but not an empty list either: appsStatus 'forbidden' lets
        // consumers say so. Branch on the classified kind, not on the wording
        // of the server's message: the two 403 bodies the API can send here
        // are phrased differently.
        const isForbidden = classifyError(err).kind === 'forbidden';
        setAppsError(isForbidden ? null : err);
        setAppsStatus(isForbidden ? 'forbidden' : 'error');
        console.error('Error loading organization apps:', err);
      } finally {
        setAppsLoading(false);
        if (!background) {
          setIsLoading(false);
        }
      }
    },
    [accessToken],
  );

  const refreshOrganizations = useCallback(async (options?: { background?: boolean }) => {
    await refreshUser(options);
  }, [refreshUser]);

  const refreshOrganizationApps = useCallback(
    async (organizationId: string, options?: { includeCognitoDetails?: boolean }) => {
      await loadAppsForOrganization(organizationId, {
        background: true,
        includeCognitoDetails: options?.includeCognitoDetails === true,
      });
    },
    [loadAppsForOrganization],
  );

  const createOrganization = useCallback(async (name: string, licenseKey: string): Promise<Organization> => {
    if (!accessToken) {
      throw new Error('Access token is required');
    }

    // A failure here belongs to the Create organization dialog's own form
    // error and is never copied into context state (it used to reappear in the
    // shell banner under "Couldn't load your organizations").
    try {
      setIsLoading(true);
      const newOrg = await apiService.createOrganization(name, licenseKey, accessToken);

      // Add the new organization to the list *before* selecting it. The
      // selection effect below re-runs on every new `organizations` array
      // identity, and `loadAppsForOrganization` produces one on every
      // successful load — so a selection that is not yet findable in the list
      // gets demoted to the stored (previous) organization, which is then
      // written back to storage and never recovers. Seeding it here closes
      // that window: the membership refresh replaces this entry with the
      // server's own copy a moment later.
      setOrganizations((prevOrganizations) =>
        prevOrganizations.some((organization) => organization.id === newOrg.id)
          ? prevOrganizations
          : [...prevOrganizations, newOrg],
      );

      // Select newly created organization and then refresh memberships.
      // Pages react to selectedOrganization from context — no window events.
      noteOrganizationSwitch();
      setSelectedOrganization(newOrg);
      await refreshOrganizations();

      return newOrg;
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, refreshOrganizations]);

  const updateAppInOrganization = useCallback((organizationId: string, appId: string, updatedApp: App) => {
    setOrganizations(prevOrgs =>
      prevOrgs.map(org =>
        org.id === organizationId
          ? {
              ...org,
              apps: org.apps.map(app => (app.id === appId ? updatedApp : app)),
            }
          : org
      )
    );
  }, []);

  const addAppToOrganization = useCallback((organizationId: string, newApp: App) => {
    setOrganizations(prevOrgs =>
      prevOrgs.map(org =>
        org.id === organizationId
          ? {
              ...org,
              apps: [...org.apps, newApp],
            }
          : org
      )
    );
  }, []);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations, userLoading]);

  useEffect(() => {
    if (!selectedOrganization?.id) {
      return;
    }
    loadAppsForOrganization(selectedOrganization.id);
  }, [loadAppsForOrganization, selectedOrganization?.id]);

  // Update selected organization when organizations list changes.
  // Prefer current selection, then restored selection from storage, then fallback to the first available organization.
  useEffect(() => {
    if (organizations.length === 0) {
      setSelectedOrganization(null);
      return;
    }

    const storedOrganizationId = getStoredSelectedOrganizationId(selectedOrganizationStorageKey);

    setSelectedOrganization((currentOrganization) => {
      if (currentOrganization) {
        const updatedOrganization = organizations.find((org) => org.id === currentOrganization.id);
        if (updatedOrganization) {
          return updatedOrganization;
        }
      }

      if (storedOrganizationId) {
        const storedOrganization = organizations.find((org) => org.id === storedOrganizationId);
        if (storedOrganization) {
          return storedOrganization;
        }
      }

      return organizations[0];
    });
  }, [organizations, selectedOrganizationStorageKey]);

  useEffect(() => {
    if (selectedOrganization?.id) {
      setStoredSelectedOrganizationId(selectedOrganizationStorageKey, selectedOrganization.id);
    }
  }, [selectedOrganization?.id, selectedOrganizationStorageKey]);

  // Memoised like the sibling UserContext: without it every consumer re-renders
  // on any state change here, and an effect keyed on one of these callbacks
  // would loop forever — the callback sets state in this provider, the provider
  // re-renders, and the callback comes back with a new identity.
  const value = useMemo<OrganizationContextType>(
    () => ({
      selectedOrganization,
      organizations,
      setSelectedOrganization,
      isLoading,
      appsLoading,
      membershipError,
      appsError,
      appsStatus,
      refreshOrganizations,
      refreshOrganizationApps,
      createOrganization,
      updateAppInOrganization,
      addAppToOrganization,
    }),
    [
      addAppToOrganization,
      appsLoading,
      appsError,
      appsStatus,
      createOrganization,
      isLoading,
      membershipError,
      organizations,
      refreshOrganizationApps,
      refreshOrganizations,
      selectedOrganization,
      updateAppInOrganization,
    ],
  );

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = (): OrganizationContextType => {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};
